import styles from '../agents.module.css';

import clsxm from '@/lib/clsxm';

import { SourceRefs } from './kit';
import type { AlternativePageProps, ResolvedDifference } from './types';
import { Visual } from './visuals';
import { SectionHeading, Stage, tone } from '../primitives';
import { useReveal } from '../useReveal';

const WORDS = ['two', 'three', 'four', 'five'];

/** The real differences, each beside a mock-up of our product. */
export default function Differences({
  differences,
  ours,
}: Pick<AlternativePageProps, 'differences' | 'ours'>) {
  const count = WORDS[differences.length - 2] ?? String(differences.length);

  return (
    <section id='differences' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title='What’s actually different.'
          tail={`The ${count} that matter most.`}
        />
        <div className='mt-16 space-y-20 sm:mt-20 sm:space-y-28'>
          {differences.map((item, index) => (
            <DifferenceRow
              key={item.title}
              item={item}
              index={index}
              ours={ours}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function DifferenceRow({
  item,
  index,
  ours,
}: {
  item: ResolvedDifference;
  index: number;
  ours: AlternativePageProps['ours'];
}) {
  const { ref, state } = useReveal<HTMLDivElement>(0.2);
  const flipped = index % 2 === 1;

  return (
    <div className='grid items-center gap-10 lg:grid-cols-12 lg:gap-16'>
      <div
        className={clsxm(
          'min-w-0 lg:col-span-5',
          flipped && 'lg:order-2 lg:col-start-8',
        )}
      >
        <span
          className={clsxm(
            'inline-flex h-9 min-w-[2.25rem] items-center justify-center rounded-full px-3 text-sm font-semibold tabular-nums',
            'bg-primary-50 text-primary-700 dark:bg-primary-400/10 dark:text-primary-300',
          )}
        >
          {String(index + 1).padStart(2, '0')}
        </span>
        <h3
          className={clsxm(
            styles.display,
            'mt-5 text-[1.75rem] font-semibold leading-[1.12] sm:text-[2.125rem]',
            tone.ink,
          )}
        >
          {item.title}
        </h3>
        <p className={clsxm('mt-5 text-[17px] leading-8', tone.body)}>
          {item.body}
          <SourceRefs ids={item.sources} />
        </p>
      </div>

      <div
        ref={ref}
        data-reveal={state}
        className={clsxm(
          styles.reveal,
          'min-w-0 lg:col-span-7',
          flipped && 'lg:order-1',
        )}
      >
        <Stage
          variant='lavender'
          halftoneAt={flipped ? '0% 100%' : '100% 0%'}
          className='-mx-3 px-4 py-8 sm:mx-0 sm:px-10 sm:py-12'
        >
          <div aria-hidden='true' className='mx-auto max-w-[30rem]'>
            <Visual kind={item.visual} ours={ours} />
          </div>
        </Stage>
      </div>
    </div>
  );
}
